/*
 * File: assets/store-locator.js
 * Scopo: Store Locator Dog Heroes — clustering, geocoding search, filtro catena/raggio
 * Dipendenze: Google Maps JS API (lazy loaded), MarkerClusterer (CDN)
 *
 * ── FIX FILTRI (giugno 2026) ──────────────────────────────────────────────
 * Bug: applyFilters() tagliava i risultati a `maxResults` (10) e questo set
 * ridotto veniva usato SIA per la lista SIA per i marker della mappa. Appena
 * si applicava un filtro (catena/prodotto) si vedevano solo 10 punti su N e la
 * mappa zoomava su 10 punti arbitrari (ordinati per città).
 * Correzione: applyFilters() ora restituisce TUTTI i punti corrispondenti. Il
 * limite `maxResults` viene applicato SOLO alla lista e SOLO nello stato
 * "sfoglia tutto" (nessun filtro/ricerca attivi); con un filtro/ricerca attivi
 * lista e mappa mostrano tutti i punti corrispondenti.
 * ─────────────────────────────────────────────────────────────────────────
 */

(function () {
  'use strict';

  const INSTANCES = new Map();
  let googleMapsLoading = false;
  let googleMapsLoaded = false;
  const googleMapsCallbacks = [];
  let markerClustererLoaded = false;

  /* =========================================================================
     Load Google Maps + MarkerClusterer
     ========================================================================= */

  function loadGoogleMaps(apiKey) {
    return new Promise((resolve, reject) => {
      if (googleMapsLoaded && window.google?.maps) {
        resolve(window.google.maps);
        return;
      }
      googleMapsCallbacks.push({ resolve, reject });
      if (googleMapsLoading) return;
      googleMapsLoading = true;

      if (!apiKey) {
        googleMapsLoading = false;
        const err = new Error('Google Maps API key not configured');
        googleMapsCallbacks.forEach((cb) => cb.reject(err));
        googleMapsCallbacks.length = 0;
        return;
      }

      const script = document.createElement('script');
      script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=marker&loading=async&callback=__dghMapsInit`;
      script.async = true;
      script.defer = true;

      window.__dghMapsInit = function () {
        googleMapsLoaded = true;
        googleMapsLoading = false;
        googleMapsCallbacks.forEach((cb) => cb.resolve(window.google.maps));
        googleMapsCallbacks.length = 0;
        delete window.__dghMapsInit;
        loadMarkerClusterer();
      };

      script.onerror = function () {
        googleMapsLoading = false;
        const err = new Error('Failed to load Google Maps API');
        googleMapsCallbacks.forEach((cb) => cb.reject(err));
        googleMapsCallbacks.length = 0;
      };

      document.head.appendChild(script);
    });
  }

  function loadMarkerClusterer() {
    if (markerClustererLoaded || window.markerClusterer) {
      // Già disponibile: applica subito il clustering ai locator esistenti.
      INSTANCES.forEach((inst) => inst._onClustererReady && inst._onClustererReady());
      return;
    }
    const s = document.createElement('script');
    // Self-hosted come asset del tema (niente terza origine unpkg → più veloce e
    // affidabile). Fallback all'unpkg CDN se l'asset non è configurato.
    s.src = window.__dghClustererUrl || 'https://unpkg.com/@googlemaps/markerclusterer@2.5.3/dist/index.min.js';
    s.async = true;
    s.onload = function () {
      markerClustererLoaded = true;
      // FIX: la libreria di clustering arriva in modo asincrono, spesso DOPO il
      // primo render dei marker. Quando è pronta, (ri)applica il clustering a
      // tutte le istanze della mappa già inizializzate.
      INSTANCES.forEach((inst) => inst._onClustererReady && inst._onClustererReady());
    };
    s.onerror = function () {
      console.warn('[StoreLocator] MarkerClusterer non caricato: marker mostrati singolarmente.');
    };
    document.head.appendChild(s);
  }

  /* =========================================================================
     Utilities
     ========================================================================= */

  function debounce(fn, delay) {
    let timer;
    return function (...args) {
      clearTimeout(timer);
      timer = setTimeout(() => fn.apply(this, args), delay);
    };
  }

  /* =========================================================================
     Class: DogHeroesStoreLocator
     ========================================================================= */

  class DogHeroesStoreLocator {
    constructor(container, options = {}) {
      this.container = container;
      this.options = {
        mode: container.dataset.mode || 'full',
        showMap: container.dataset.showMap !== 'false',
        prefilter: container.dataset.prefilter || '',
        maxResults: parseInt(container.dataset.maxResults, 10) || 10,
        showSearch: container.dataset.showSearch !== 'false',
        radiusKm: parseInt(container.dataset.radiusKm, 10) || 50,
        allowGeolocation: container.dataset.allowGeolocation !== 'false',
        apiKey: container.dataset.apiKey || '',
        dataUrl: container.dataset.dataUrl || '',
        shopUrl: container.dataset.shopUrl || '/',
        showFilters: container.dataset.showFilters !== 'false',
        mapHeight: parseInt(container.dataset.mapHeight, 10) || 500,
        mapPosition: container.dataset.mapPosition || 'right',
        showBuyOnline: container.dataset.showBuyOnline !== 'false',
        labelRetail: container.dataset.labelRetail || 'Negozio',
        labelRivenditore: container.dataset.labelRivenditore || 'Rivenditore',
        labelVeterinario: container.dataset.labelVeterinario || 'Clinica Veterinaria',
        ...options,
      };

      this.locations = [];
      this.filteredLocations = [];
      this.activeFilter = this.options.prefilter || '';
      this.activeChains = new Set(); // multi-select chains
      this.activeProducts = new Set(); // multi-select product tags
      this.activeBrands = new Set(); // multi-select marchio (dog / cat)
      this.map = null;
      this.markers = [];
      this.clusterer = null;
      this.clusterers = [];
      this.infoWindow = null;
      this.center = null;
      this.activeCardId = null;
      this.mapVisible = true;
      this._searchActive = false; // FIX: true quando è attiva una ricerca testuale
      this._totalMatched = 0;     // FIX: n. totale di punti che soddisfano i filtri
      this._refs = {};
    }

    /* -----------------------------------------------------------------------
       Init
       ----------------------------------------------------------------------- */

    init() {
      this._cacheRefs();
      this._bindEvents();
      this._applyMode();
      this._setMapHeight();
      this.loadLocations();
      this._observeForMap();

      const id = this.container.id || `sl-${INSTANCES.size}`;
      INSTANCES.set(id, this);
    }

    _cacheRefs() {
      const c = this.container;
      this._refs.searchInput = c.querySelector('[data-sl-search]');
      this._refs.geoBtn = c.querySelector('[data-sl-geo]');
      this._refs.typeSelect = c.querySelector('[data-sl-type]');
      this._refs.radiusSelect = c.querySelector('[data-sl-radius]');
      this._refs.chainFilters = c.querySelector('[data-sl-chain-filters]');
      this._refs.retailers = c.querySelector('[data-sl-retailers]');
      this._refs.productFilters = c.querySelector('[data-sl-product-filters]');
      this._refs.brandFilters = c.querySelector('[data-sl-brand-filters]');
      this._refs.resultsList = c.querySelector('[data-sl-results]');
      this._refs.resultsCount = c.querySelector('[data-sl-results-count]');
      this._refs.mapEl = c.querySelector('[data-sl-map]');
      this._refs.mapWrap = c.querySelector('[data-sl-map-wrap]');
      this._refs.mapToggle = c.querySelector('[data-sl-map-toggle]');
      this._refs.loading = c.querySelector('[data-sl-loading]');
      this._refs.empty = c.querySelector('[data-sl-empty]');
    }

    _bindEvents() {
      if (this._refs.searchInput) {
        // Debounced text search + geocoding
        this._refs.searchInput.addEventListener(
          'input',
          debounce((e) => this._handleSearchInput(e.target.value), 300)
        );
        // Enter key triggers immediate geocoding
        this._refs.searchInput.addEventListener('keydown', (e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            this._geocodeAndSearch(e.target.value.trim());
          }
        });
      }

      if (this._refs.geoBtn) {
        this._refs.geoBtn.addEventListener('click', () => this.handleGeolocation());
      }

      // Type select (in toolbar)
      if (this._refs.typeSelect) {
        this._refs.typeSelect.addEventListener('change', (e) => {
          this.activeFilter = e.target.value;
          this._applyAndRender();
        });
      }

      if (this._refs.radiusSelect) {
        this._refs.radiusSelect.addEventListener('change', (e) => {
          this.options.radiusKm = parseInt(e.target.value, 10);
          if (this.center) {
            this._applyAndRender();
          }
        });
      }

      // Chain filter — pill catena (se presenti)
      if (this._refs.chainFilters) {
        this._refs.chainFilters.addEventListener('click', (e) => {
          const btn = e.target.closest('[data-sl-chain-filter]');
          if (btn) this._toggleChain(btn.dataset.slChainFilter);
        });
      }

      // Banner rosso loghi retailer — funge da filtro per catena
      if (this._refs.retailers) {
        this._refs.retailers.addEventListener('click', (e) => {
          const btn = e.target.closest('[data-sl-chain-filter]');
          if (btn) this._toggleChain(btn.dataset.slChainFilter);
        });
      }

      // Product pills (multi-select)
      if (this._refs.productFilters) {
        this._refs.productFilters.addEventListener('click', (e) => {
          const btn = e.target.closest('[data-sl-product-filter]');
          if (!btn) return;
          const value = btn.dataset.slProductFilter;

          if (value === 'all') {
            this.activeProducts.clear();
          } else {
            if (this.activeProducts.has(value)) {
              this.activeProducts.delete(value);
            } else {
              this.activeProducts.add(value);
            }
          }

          this._updateProductPillStates();
          this._applyAndRender();
        });
      }

      // Brand filter — loghi DogHeroes / CatHeroes (multi-select)
      if (this._refs.brandFilters) {
        this._refs.brandFilters.addEventListener('click', (e) => {
          const btn = e.target.closest('[data-sl-brand-filter]');
          if (btn) this._toggleBrand(btn.dataset.slBrandFilter);
        });
      }

      if (this._refs.mapToggle) {
        this._refs.mapToggle.addEventListener('click', () => this._toggleMap());
      }

      // Chiude i tooltip info quando si clicca/tocca altrove
      document.addEventListener('click', (e) => {
        if (!this._refs.resultsList) return;
        if (e.target.closest && e.target.closest('[data-sl-info]')) return;
        this._refs.resultsList.querySelectorAll('[data-sl-info].is-open').forEach((o) => o.classList.remove('is-open'));
      });
    }

    _applyMode() {
      const mode = this.options.mode;
      this.container.classList.toggle('store-locator--compact', mode === 'compact');
      this.container.classList.toggle('store-locator--map-only', mode === 'map-only');
      this.container.classList.toggle('store-locator--list-only', mode === 'list-only');
    }

    _setMapHeight() {
      if (this._refs.mapEl) {
        this.container.style.setProperty('--sl-map-height', `${this.options.mapHeight}px`);
      }
    }

    /* -----------------------------------------------------------------------
       Helper: c'è un filtro o una ricerca attiva?  (FIX)
       ----------------------------------------------------------------------- */

    _hasActiveQuery() {
      return (
        this.activeChains.size > 0 ||
        this.activeProducts.size > 0 ||
        this.activeBrands.size > 0 ||
        !!this.activeFilter ||
        !!this.center ||
        !!this._searchActive
      );
    }

    /* -----------------------------------------------------------------------
       Data Loading
       ----------------------------------------------------------------------- */

    async loadLocations() {
      this._showLoading(true);
      try {
        const url = this.options.dataUrl;
        if (!url) throw new Error('No data URL configured');
        const resp = await fetch(url);
        if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
        const parsed = await resp.json();
        // Supporta due forme: array "piatto" (legacy) oppure oggetto
        // { icons: {catena: logoUrl}, stores: [...] } (ottimizzato: i loghi
        // catena sono deduplicati in una mappa invece di ripetersi su ogni store).
        if (Array.isArray(parsed)) {
          this.locations = parsed;
          this._chainIcons = {};
        } else {
          this.locations = parsed.stores || [];
          this._chainIcons = parsed.icons || {};
        }
        // Sort by city so the maxResults cap doesn't always show the first chain in the JSON.
        this.locations.sort((a, b) => (a.city || '').localeCompare(b.city || '', 'it'));
        this._assignPinIcons();
        this._populateChainPills();
        this._populateRetailers();
        this._populateProductPills();
        this._populateBrandPills();
        // Initial view: show all locations (no center/radius filter)
        this.filteredLocations = this.applyFilters(this.locations);
        this.renderResults(this.filteredLocations);
        this._updateOpenStatus();
      } catch (err) {
        console.error('[StoreLocator] Failed to load locations:', err);
        this.locations = [];
        this.renderEmptyState();
      } finally {
        this._showLoading(false);
      }
    }

    _assignPinIcons() {
      const c = this.container;
      const pinMap = {
        'Arcaplanet': c.dataset.pinArcaplanet || '',
        'Conad Pet Store': c.dataset.pinConad || '',
        'Finiper': c.dataset.pinFiniper || '',
        'Giulius': c.dataset.pinGiulius || '',
        'Isola dei Tesori': c.dataset.pinIsola || '',
        '': c.dataset.pinIndipendenti || '',
      };
      this._pinIndip = c.dataset.pinIndipendenti || '';
      this._chainPins = {};
      this.locations.forEach((loc) => {
        // Fallback al pin indipendenti per catene senza pin (es. Dog&Company)
        loc.pin = pinMap[loc.chain] || this._pinIndip || '';
        const key = loc.chain || '';
        if (!this._chainPins[key]) this._chainPins[key] = loc.pin;
      });
    }

    // Logo catena: preferisce il campo per-store `icon` (legacy), altrimenti la
    // mappa deduplicata `icons[chain]` dal JSON ottimizzato.
    _iconFor(loc) {
      if (loc && loc.icon) return loc.icon;
      if (loc && this._chainIcons) return this._chainIcons[loc.chain] || '';
      return '';
    }

    _populateChainPills() {
      if (!this._refs.chainFilters) return;
      const chains = new Set();
      this.locations.forEach((loc) => {
        if (loc.chain) chains.add(loc.chain);
      });
      // Also count independents
      const hasIndependents = this.locations.some((loc) => !loc.chain);

      const sorted = [...chains].sort();
      this._refs.chainFilters.innerHTML = '';

      // "Tutte" pill
      const allBtn = document.createElement('button');
      allBtn.type = 'button';
      allBtn.className = 'store-locator__filter-btn store-locator__filter-btn--active';
      allBtn.dataset.slChainFilter = 'all';
      allBtn.textContent = 'Tutte';
      this._refs.chainFilters.appendChild(allBtn);

      sorted.forEach((chain) => {
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'store-locator__filter-btn store-locator__filter-btn--active';
        btn.dataset.slChainFilter = chain;
        btn.textContent = chain;
        this._refs.chainFilters.appendChild(btn);
      });

      if (hasIndependents) {
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'store-locator__filter-btn store-locator__filter-btn--active';
        btn.dataset.slChainFilter = '';
        btn.textContent = 'Indipendenti';
        this._refs.chainFilters.appendChild(btn);
      }
    }

    _updateChainPillStates() {
      if (!this._refs.chainFilters) return;
      const noFilter = this.activeChains.size === 0;

      this._refs.chainFilters.querySelectorAll('[data-sl-chain-filter]').forEach((btn) => {
        const val = btn.dataset.slChainFilter;
        if (val === 'all') {
          btn.classList.toggle('store-locator__filter-btn--active', noFilter);
        } else {
          btn.classList.toggle('store-locator__filter-btn--active', noFilter || this.activeChains.has(val));
        }
      });
    }

    /* -----------------------------------------------------------------------
       Chain toggle condiviso (pill catena + banner loghi retailer)
       ----------------------------------------------------------------------- */

    _toggleChain(value) {
      if (value == null) return;
      if (value === 'all') {
        this.activeChains.clear();
      } else if (this.activeChains.has(value)) {
        this.activeChains.delete(value);
      } else {
        this.activeChains.add(value);
      }
      this._updateChainPillStates();
      this._updateRetailerStates();
      this._applyAndRender();
    }

    /* -----------------------------------------------------------------------
       Banner rosso loghi retailer (filtro per catena)
       ----------------------------------------------------------------------- */

    _populateRetailers() {
      if (!this._refs.retailers) return;
      const logos = {};
      const chains = new Set();
      this.locations.forEach((loc) => {
        if (loc.chain) {
          chains.add(loc.chain);
          if (!logos[loc.chain]) { const ic = this._iconFor(loc); if (ic) logos[loc.chain] = ic; }
        }
      });
      const hasIndependents = this.locations.some((loc) => !loc.chain);
      const sorted = [...chains].sort();
      this._refs.retailers.innerHTML = '';

      const makeBtn = (value, label, logo) => {
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'sl-retailers__btn sl-retailers__btn--active';
        btn.dataset.slChainFilter = value;
        btn.title = label;
        btn.setAttribute('aria-label', label);
        if (logo) {
          btn.classList.add('sl-retailers__btn--logo');
          const img = document.createElement('img');
          img.src = logo;
          img.alt = label;
          img.loading = 'lazy';
          btn.appendChild(img);
        } else {
          btn.classList.add('sl-retailers__btn--text');
          btn.textContent = label;
        }
        this._refs.retailers.appendChild(btn);
      };

      makeBtn('all', 'Tutti');
      sorted.forEach((chain) => makeBtn(chain, chain, logos[chain]));
      if (hasIndependents) makeBtn('', 'Indipendenti');

      // Salva catena→logo e pre-carica le dimensioni dei loghi: i cluster
      // useranno lo STESSO logo del filtro (corrispondenza filtro ↔ mappa).
      this._chainLogos = logos;
      this._preloadLogoDims();
    }

    _preloadLogoDims() {
      this._logoDims = this._logoDims || {};
      Object.keys(this._chainLogos || {}).forEach((chain) => {
        const url = this._chainLogos[chain];
        if (!url || this._logoDims[chain]) return;
        const img = new Image();
        img.onload = () => { this._logoDims[chain] = { w: img.naturalWidth, h: img.naturalHeight }; };
        img.src = url;
      });
    }

    _updateRetailerStates() {
      if (!this._refs.retailers) return;
      const noFilter = this.activeChains.size === 0;
      this._refs.retailers.querySelectorAll('[data-sl-chain-filter]').forEach((btn) => {
        const val = btn.dataset.slChainFilter;
        if (val === 'all') {
          btn.classList.toggle('sl-retailers__btn--active', noFilter);
        } else {
          btn.classList.toggle('sl-retailers__btn--active', noFilter || this.activeChains.has(val));
        }
      });
    }

    /* -----------------------------------------------------------------------
       Filtro per marchio — loghi DogHeroes (dog) / CatHeroes (cat)
       Il dato è il campo `brands` di ogni punto vendita (es. ["dog"],
       ["dog","cat"], ["cat"]). Se assente/vuoto → default ["dog"]
       (retrocompatibile: i dati attuali restano "solo cane").
       ----------------------------------------------------------------------- */

    _brandMeta() {
      const c = this.container;
      return {
        dog: { label: c.dataset.brandLabelDog || 'DogHeroes', logo: c.dataset.brandLogoDog || '' },
        cat: { label: c.dataset.brandLabelCat || 'CatHeroes', logo: c.dataset.brandLogoCat || '' },
      };
    }

    _brandsForLoc(loc) {
      const b = Array.isArray(loc.brands) ? loc.brands.filter(Boolean) : [];
      return b.length ? b : ['dog'];
    }

    _populateBrandPills() {
      if (!this._refs.brandFilters) return;
      const meta = this._brandMeta();
      const section = this._refs.brandFilters.closest('[data-sl-brand-section]');

      // Solo i marchi realmente presenti nei dati, in ordine dog → cat.
      const present = new Set();
      this.locations.forEach((loc) => this._brandsForLoc(loc).forEach((b) => present.add(b)));
      const order = ['dog', 'cat'].filter((b) => present.has(b));

      // Con un solo marchio il filtro è inutile → sezione nascosta.
      if (order.length < 2) {
        if (section) section.hidden = true;
        return;
      }
      if (section) section.hidden = false;

      this._refs.brandFilters.innerHTML = '';

      const makeBtn = (value, label, logo) => {
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'sl-brands__btn sl-brands__btn--active';
        btn.dataset.slBrandFilter = value;
        btn.title = label;
        btn.setAttribute('aria-label', label);
        if (logo) {
          btn.classList.add('sl-brands__btn--logo');
          const img = document.createElement('img');
          img.src = logo;
          img.alt = label;
          img.loading = 'lazy';
          btn.appendChild(img);
        } else {
          btn.classList.add('sl-brands__btn--text');
          btn.textContent = label;
        }
        this._refs.brandFilters.appendChild(btn);
      };

      // Nessun bottone "Tutti": con due sole scelte, cliccare un marchio filtra,
      // ri-cliccarlo (toggle off) torna a mostrare tutti i punti vendita.
      order.forEach((b) => makeBtn(b, meta[b].label, meta[b].logo));
    }

    _toggleBrand(value) {
      if (value == null) return;
      if (value === 'all') {
        this.activeBrands.clear();
      } else if (this.activeBrands.has(value)) {
        this.activeBrands.delete(value);
      } else {
        this.activeBrands.add(value);
      }
      this._updateBrandStates();
      this._applyAndRender();
    }

    _updateBrandStates() {
      if (!this._refs.brandFilters) return;
      const noFilter = this.activeBrands.size === 0;
      this._refs.brandFilters.querySelectorAll('[data-sl-brand-filter]').forEach((btn) => {
        const val = btn.dataset.slBrandFilter;
        if (val === 'all') {
          btn.classList.toggle('sl-brands__btn--active', noFilter);
        } else {
          btn.classList.toggle('sl-brands__btn--active', noFilter || this.activeBrands.has(val));
        }
      });
    }

    // Product image map — maps tag name to Shopify asset filename
    _getProductImageUrl(tag) {
      const map = {
        'Surgelato': 'sl-product-surgelato.png',
        'Dispensa': 'sl-product-dispensa.png',
        'Snack': 'sl-product-snack.png',
        'Cibo fresco': 'sl-product-fresco.png',
        'CatHeroes': 'sl-product-cat.png',
      };
      const file = map[tag];
      if (!file) return '';
      // Build Shopify asset URL from data-url base
      const baseUrl = this.options.dataUrl || '';
      const assetBase = baseUrl.substring(0, baseUrl.lastIndexOf('/') + 1);
      return assetBase + file;
    }

    _populateProductPills() {
      if (!this._refs.productFilters) return;
      const products = new Set();
      this.locations.forEach((loc) => {
        if (loc.tags) loc.tags.forEach((t) => products.add(t));
      });
      const sorted = [...products].sort();
      this._refs.productFilters.innerHTML = '';

      // "Tutti" pill
      const allBtn = document.createElement('button');
      allBtn.type = 'button';
      allBtn.className = 'store-locator__filter-btn store-locator__filter-btn--active';
      allBtn.dataset.slProductFilter = 'all';
      allBtn.textContent = 'Tutti';
      this._refs.productFilters.appendChild(allBtn);

      sorted.forEach((product) => {
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'store-locator__filter-btn store-locator__filter-btn--active';
        btn.dataset.slProductFilter = product;

        const imgUrl = this._getProductImageUrl(product);
        if (imgUrl) {
          btn.innerHTML = `<img class="sl-filter-icon" src="${imgUrl}" alt="${product}"> ${product}`;
        } else {
          btn.textContent = product;
        }

        this._refs.productFilters.appendChild(btn);
      });
    }

    _updateProductPillStates() {
      if (!this._refs.productFilters) return;
      const noFilter = this.activeProducts.size === 0;

      this._refs.productFilters.querySelectorAll('[data-sl-product-filter]').forEach((btn) => {
        const val = btn.dataset.slProductFilter;
        if (val === 'all') {
          btn.classList.toggle('store-locator__filter-btn--active', noFilter);
        } else {
          btn.classList.toggle('store-locator__filter-btn--active', noFilter || this.activeProducts.has(val));
        }
      });
    }

    /* -----------------------------------------------------------------------
       Map Init — lazy via IntersectionObserver
       ----------------------------------------------------------------------- */

    _observeForMap() {
      if (!this.options.showMap || this.options.mode === 'list-only') return;
      if (!this._refs.mapEl) return;
      const observer = new IntersectionObserver(
        (entries) => {
          if (entries[0].isIntersecting) {
            observer.disconnect();
            this.initMap();
          }
        },
        { rootMargin: '200px' }
      );
      observer.observe(this._refs.mapEl);
    }

    async initMap() {
      try {
        await loadGoogleMaps(this.options.apiKey);

        // Default: show all of Italy
        this.map = new google.maps.Map(this._refs.mapEl, {
          center: { lat: 42.5, lng: 12.5 },
          zoom: 6,
          mapTypeControl: false,
          streetViewControl: false,
          fullscreenControl: true,
          zoomControl: true,
          styles: [
            { featureType: 'poi', stylers: [{ visibility: 'off' }] },
            { featureType: 'transit', stylers: [{ visibility: 'simplified' }] },
          ],
        });

        this.infoWindow = new google.maps.InfoWindow();

        // FIX: se l'utente ha già applicato un filtro/ricerca prima che la mappa
        // si inizializzasse (lazy load), mostra i marker filtrati, non tutti.
        if (this._hasActiveQuery()) {
          this._updateMapMarkers();
        } else {
          this._addAllMarkers();
        }

      } catch (err) {
        console.warn('[StoreLocator] Map unavailable:', err.message);
        this._showMapFallback();
      }
    }

    _showMapFallback() {
      if (!this._refs.mapEl) return;
      this._refs.mapEl.innerHTML = `
        <div class="store-locator__map-fallback">
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
            <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z"/>
            <circle cx="12" cy="9" r="2.5"/>
          </svg>
          <span>La mappa non è disponibile. Usa la lista per trovare un punto vendita.</span>
        </div>
      `;
    }

    /* -----------------------------------------------------------------------
       Search — Geocoding + text fallback
       ----------------------------------------------------------------------- */

    _handleSearchInput(query) {
      const q = query.trim();
      if (!q) {
        // Reset to show all
        this._searchActive = false;
        this.center = null;
        this._applyAndRender();
        this._addAllMarkers();
        return;
      }
      // Text-only filtering (instant, while typing)
      this._textSearch(q);
    }

    _textSearch(query) {
      const q = query.toLowerCase();
      this._searchActive = true;
      const results = this.locations.filter((loc) =>
        (loc.name || '').toLowerCase().includes(q) ||
        (loc.city || '').toLowerCase().includes(q) ||
        String(loc.zip || '').includes(q) ||
        (loc.address || '').toLowerCase().includes(q) ||
        (loc.chain && loc.chain.toLowerCase().includes(q)) ||
        (loc.tags && loc.tags.some((t) => t.toLowerCase().includes(q)))
      );
      this.filteredLocations = this.applyFilters(results);
      this.renderResults(this.filteredLocations);
      this._updateMapMarkers();
      this._updateOpenStatus();
    }

    async _geocodeAndSearch(query) {
      if (!query) return;

      const apiKey = this.options.apiKey;
      if (!apiKey) {
        this._textSearch(query);
        return;
      }

      try {
        const url = `https://maps.googleapis.com/maps/api/geocode/json?address=${encodeURIComponent(query + ', Italia')}&key=${apiKey}`;
        const resp = await fetch(url);
        const data = await resp.json();

        if (data.status === 'OK' && data.results.length > 0) {
          const loc = data.results[0].geometry.location;
          this.center = { lat: loc.lat, lng: loc.lng };
          this._applyAndRender();

          if (this.map) {
            this.map.panTo(this.center);
            this.map.setZoom(11);
          }
          return;
        }
      } catch (err) {
        console.warn('[StoreLocator] Geocoding failed:', err);
      }

      // Fallback to text search
      this._textSearch(query);
    }

    handleSearch(query) {
      this._handleSearchInput(query);
    }

    /* -----------------------------------------------------------------------
       Geolocation
       ----------------------------------------------------------------------- */

    handleGeolocation() {
      if (!navigator.geolocation) {
        this._geoError('Il tuo browser non supporta la geolocalizzazione.');
        return;
      }

      if (this._refs.geoBtn) {
        this._refs.geoBtn.disabled = true;
        this._refs.geoBtn.setAttribute('aria-busy', 'true');
      }

      navigator.geolocation.getCurrentPosition(
        (pos) => {
          this.center = { lat: pos.coords.latitude, lng: pos.coords.longitude };
          this._applyAndRender();

          if (this.map) {
            this.map.panTo(this.center);
            this.map.setZoom(11);
          }

          if (this._refs.geoBtn) {
            this._refs.geoBtn.disabled = false;
            this._refs.geoBtn.removeAttribute('aria-busy');
          }
        },
        () => {
          this._geoError('Geolocalizzazione non disponibile. Puoi cercare per città o CAP.');
          if (this._refs.geoBtn) {
            this._refs.geoBtn.disabled = false;
            this._refs.geoBtn.removeAttribute('aria-busy');
          }
        },
        { enableHighAccuracy: false, timeout: 8000, maximumAge: 300000 }
      );
    }

    _geoError(msg) {
      if (this._refs.searchInput) {
        this._refs.searchInput.focus();
        this._refs.searchInput.setAttribute('placeholder', msg);
        setTimeout(() => {
          this._refs.searchInput.setAttribute(
            'placeholder',
            this._refs.searchInput.dataset.placeholder || ''
          );
        }, 4000);
      }
    }

    /* -----------------------------------------------------------------------
       Distance (Haversine)
       ----------------------------------------------------------------------- */

    calculateDistance(lat1, lng1, lat2, lng2) {
      const R = 6371;
      const dLat = ((lat2 - lat1) * Math.PI) / 180;
      const dLng = ((lng2 - lng1) * Math.PI) / 180;
      const a =
        Math.sin(dLat / 2) ** 2 +
        Math.cos((lat1 * Math.PI) / 180) *
          Math.cos((lat2 * Math.PI) / 180) *
          Math.sin(dLng / 2) ** 2;
      return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    }

    filterByRadius(locations, center, radiusKm) {
      return locations.filter((loc) =>
        this.calculateDistance(center.lat, center.lng, loc.lat, loc.lng) <= radiusKm
      );
    }

    /* -----------------------------------------------------------------------
       Filters
       ----------------------------------------------------------------------- */

    applyFilters(locations) {
      let result = locations;

      // Type filter
      if (this.activeFilter) {
        result = result.filter((loc) => loc.type === this.activeFilter);
      }

      // Chain filter (multi-select)
      if (this.activeChains.size > 0) {
        result = result.filter((loc) => this.activeChains.has(loc.chain || ''));
      }

      // Product filter (multi-select)
      if (this.activeProducts.size > 0) {
        result = result.filter((loc) =>
          loc.tags && loc.tags.some((t) => this.activeProducts.has(t))
        );
      }

      // Brand filter (dog/cat) — multi-select
      if (this.activeBrands.size > 0) {
        result = result.filter((loc) =>
          this._brandsForLoc(loc).some((b) => this.activeBrands.has(b))
        );
      }

      // FIX: nessun cap qui. applyFilters restituisce TUTTI i punti che
      // soddisfano i filtri. Il limite `maxResults` viene applicato solo alla
      // lista, e solo nello stato "sfoglia tutto" (vedi renderResults).
      this._totalMatched = result.length;
      return result;
    }

    setPrefilter(filterValue) {
      this.activeFilter = filterValue;
      if (this._refs.typeSelect) this._refs.typeSelect.value = filterValue;
      this._applyAndRender();
    }

    _applyAndRender() {
      let results = this.center
        ? this.filterByRadius(this.locations, this.center, this.options.radiusKm)
        : [...this.locations];

      if (this.center) {
        results.forEach((loc) => {
          loc._distance = this.calculateDistance(this.center.lat, this.center.lng, loc.lat, loc.lng);
        });
        results.sort((a, b) => a._distance - b._distance);
      }

      this.filteredLocations = this.applyFilters(results);
      this.renderResults(this.filteredLocations);
      this._updateMapMarkers();
      this._updateOpenStatus();
    }

    /* -----------------------------------------------------------------------
       Rendering
       ----------------------------------------------------------------------- */

    renderResults(locations) {
      if (!this._refs.resultsList) return;

      // FIX: `locations` ora contiene TUTTI i punti corrispondenti.
      // Cappiamo la LISTA a maxResults solo nello stato "sfoglia tutto"
      // (nessun filtro/ricerca attivi). La mappa mostra sempre tutto.
      const total = this._totalMatched != null ? this._totalMatched : locations.length;
      const capList = !this._hasActiveQuery();
      const listLocations = capList ? locations.slice(0, this.options.maxResults) : locations;

      if (this._refs.resultsCount) {
        if (total > listLocations.length) {
          this._refs.resultsCount.textContent = `${total} punti vendita — ${listLocations.length} visualizzati`;
        } else {
          this._refs.resultsCount.textContent = `${listLocations.length} ${listLocations.length === 1 ? 'punto vendita' : 'punti vendita'}`;
        }
      }

      if (listLocations.length === 0) {
        this.renderEmptyState();
        return;
      }

      if (this._refs.empty) this._refs.empty.hidden = true;
      this._refs.resultsList.innerHTML = listLocations.map((loc) => this.renderCard(loc)).join('');

      this._refs.resultsList.querySelectorAll('[data-sl-card]').forEach((card) => {
        card.addEventListener('click', (e) => {
          if (e.target.closest('a, button, details')) return;
          this._selectLocation(card.dataset.slCard);
        });
      });

      // Tooltip info: tap su mobile (toggle). Su desktop basta l'hover (CSS).
      this._refs.resultsList.querySelectorAll('[data-sl-info]').forEach((btn) => {
        btn.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          this._refs.resultsList.querySelectorAll('[data-sl-info].is-open').forEach((o) => {
            if (o !== btn) o.classList.remove('is-open');
          });
          btn.classList.toggle('is-open');
        });
      });
    }

    renderCard(location) {
      const distanceHtml = location._distance != null
        ? `<span class="store-locator-card__distance">${this.formatDistance(location._distance)}</span>`
        : '';
      const directionsUrl = this.getDirectionsUrl(location);
      const isActive = this.activeCardId === location.id;

      // Pulsante info con tooltip (hover desktop / tap mobile)
      const infoTip = 'Ti consigliamo di contattare il punto vendita per verificare la disponibilità dei nostri prodotti.';
      const infoHtml = `<button type="button" class="sl-info" data-sl-info aria-label="${this._esc(infoTip)}"><svg viewBox="0 0 24 24" aria-hidden="true"><path d="M11 7h2v2h-2zm0 4h2v6h-2zm1-9C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8z"/></svg><span class="sl-info__tip" role="tooltip">${this._esc(infoTip)}</span></button>`;

      // Chain logo or fallback badge
      const cardIcon = this._iconFor(location);
      const logoHtml = cardIcon
        ? `<img class="store-locator-card__logo" src="${this._esc(cardIcon)}" alt="${this._esc(location.chain)}" loading="lazy">`
        : (location.chain ? `<span class="store-locator-card__chain">${this._esc(location.chain)}</span>` : '');

      // Opening hours table
      const hoursHtml = this._renderHoursTable(location.opening_hours);

      // Assortimento tags with product images. La "i" info è accanto all'etichetta.
      // Se il punto vendita ha il marchio "cat" aggiungiamo un badge "CatHeroes"
      // SOLO in visualizzazione (non è un tag prodotto → non entra nel filtro
      // "Filtra per prodotto" né in applyFilters).
      const displayTags = Array.isArray(location.tags) ? location.tags.slice() : [];
      if (this._brandsForLoc(location).includes('cat')) displayTags.push('CatHeroes');
      const tagsListHtml = displayTags.length > 0
        ? `<div class="store-locator-card__tags-list">
              ${displayTags.map((t) => {
                const imgUrl = this._getProductImageUrl(t);
                const iconCls = t === 'CatHeroes' ? 'store-locator-card__tag-icon store-locator-card__tag-icon--cat' : 'store-locator-card__tag-icon';
                const iconHtml = imgUrl
                  ? `<img class="${iconCls}" src="${imgUrl}" alt="${this._esc(t)}" loading="lazy">`
                  : `<div class="store-locator-card__tag-icon--placeholder"><svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor"><circle cx="12" cy="12" r="10"/></svg></div>`;
                return `<span class="store-locator-card__tag">${iconHtml}<span class="store-locator-card__tag-name">${this._esc(t)}</span></span>`;
              }).join('')}
            </div>`
        : '';
      const tagsHtml = `<div class="store-locator-card__tags">
            <div class="store-locator-card__tags-head">
              <span class="store-locator-card__tags-label">Assortimento</span>
              ${infoHtml}
            </div>
            ${tagsListHtml}
          </div>`;

      return `
        <article
          class="store-locator-card${isActive ? ' store-locator-card--active' : ''}"
          data-sl-card="${location.id}"
          itemscope itemtype="https://schema.org/LocalBusiness"
          tabindex="0" role="button"
          aria-label="${location.name}, ${this._formatAddress(location)}"
        >
          <div class="store-locator-card__header">
            <div>
              ${location.chain ? `<span class="store-locator-card__chain-label">${this._esc(location.chain)}</span>` : ''}
              <h3 class="store-locator-card__name" itemprop="name">${this._esc(location.name)}</h3>
              <p class="store-locator-card__address" itemprop="address">
                ${this._formatAddress(location)}
              </p>
            </div>
            ${logoHtml}
          </div>
          <div class="store-locator-card__meta">
            <span class="store-locator-card__status" data-sl-status="${location.id}"></span>
            ${distanceHtml}
          </div>
          ${hoursHtml}
          ${tagsHtml}
          <div class="store-locator-card__actions">
            <a class="store-locator-card__action" href="${directionsUrl}" target="_blank" rel="noopener" aria-label="Indicazioni per ${this._esc(location.name)}">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor"><path d="M22.46 10.12L13.88 1.54a2 2 0 00-2.83-.01l-.01.01-8.58 8.58a2 2 0 000 2.83l8.58 8.58a2 2 0 002.83 0l8.58-8.58a2 2 0 00.01-2.83zM13 17h-2v-2h2v2zm0-4h-2V7h2v6z"/></svg>
              Indicazioni
            </a>
            ${location.phone ? `<a class="store-locator-card__action" href="tel:${this._esc(location.phone)}" aria-label="Chiama ${this._esc(location.name)}">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor"><path d="M6.62 10.79a15.05 15.05 0 006.59 6.59l2.2-2.2a1 1 0 011.01-.24 11.36 11.36 0 003.58.57 1 1 0 011 1V20a1 1 0 01-1 1A17 17 0 013 4a1 1 0 011-1h3.5a1 1 0 011 1c0 1.25.2 2.45.57 3.57a1 1 0 01-.25 1.02l-2.2 2.2z"/></svg>
              Chiama
            </a>` : ''}
            ${location.website ? `<a class="store-locator-card__action" href="${this._esc(location.website)}" target="_blank" rel="noopener">Sito web</a>` : ''}
          </div>
        </article>
      `;
    }

    _renderHoursTable(hours) {
      if (!hours) return '';
      const dayLabels = { mon: 'Lun', tue: 'Mar', wed: 'Mer', thu: 'Gio', fri: 'Ven', sat: 'Sab', sun: 'Dom' };
      const dayKeys = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'];
      const now = new Date();
      const todayIdx = now.getDay(); // 0=sun
      const todayKey = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'][todayIdx];

      const rows = dayKeys.map((key) => {
        const val = hours[key] || '';
        const display = val === 'closed' ? 'Chiuso' : val.replace('-', ' – ');
        const isToday = key === todayKey;
        return `<tr class="${isToday ? 'sl-hours__today' : ''}">
          <td class="sl-hours__day">${dayLabels[key]}</td>
          <td class="sl-hours__time">${display || '–'}</td>
        </tr>`;
      }).join('');

      return `
        <details class="store-locator-card__hours">
          <summary class="store-locator-card__hours-toggle">Orari di apertura</summary>
          <table class="sl-hours">${rows}</table>
        </details>
      `;
    }

    renderEmptyState() {
      if (!this._refs.resultsList) return;
      this._refs.resultsList.innerHTML = '';
      if (this._refs.empty) this._refs.empty.hidden = false;
    }

    /* -----------------------------------------------------------------------
       Open/Closed Status
       ----------------------------------------------------------------------- */

    isOpenNow(opening_hours) {
      if (!opening_hours) return { open: false, label: '' };

      const now = new Date();
      const days = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'];
      const dayNames = ['domenica', 'lunedì', 'martedì', 'mercoledì', 'giovedì', 'venerdì', 'sabato'];
      const dayKey = days[now.getDay()];
      const todayHours = opening_hours[dayKey];

      if (!todayHours || todayHours === 'closed') {
        for (let i = 1; i <= 7; i++) {
          const nextIdx = (now.getDay() + i) % 7;
          const nextHours = opening_hours[days[nextIdx]];
          if (nextHours && nextHours !== 'closed') {
            const openTime = nextHours.split('-')[0];
            const dayLabel = i === 1 ? 'domani' : dayNames[nextIdx];
            return { open: false, label: `Chiuso · Apre ${dayLabel} alle ${openTime}` };
          }
        }
        return { open: false, label: 'Chiuso' };
      }

      const [openStr, closeStr] = todayHours.split('-');
      const [openH, openM] = openStr.split(':').map(Number);
      const [closeH, closeM] = closeStr.split(':').map(Number);
      const currentMinutes = now.getHours() * 60 + now.getMinutes();
      const openMinutes = openH * 60 + openM;
      const closeMinutes = closeH * 60 + closeM;

      if (currentMinutes >= openMinutes && currentMinutes < closeMinutes) {
        return { open: true, label: `Aperto · Chiude alle ${closeStr}` };
      } else if (currentMinutes < openMinutes) {
        return { open: false, label: `Chiuso · Apre oggi alle ${openStr}` };
      }

      for (let i = 1; i <= 7; i++) {
        const nextIdx = (now.getDay() + i) % 7;
        const nextHours = opening_hours[days[nextIdx]];
        if (nextHours && nextHours !== 'closed') {
          const openTime = nextHours.split('-')[0];
          const dayLabel = i === 1 ? 'domani' : dayNames[nextIdx];
          return { open: false, label: `Chiuso · Apre ${dayLabel} alle ${openTime}` };
        }
      }
      return { open: false, label: 'Chiuso' };
    }

    _updateOpenStatus() {
      this.filteredLocations.forEach((loc) => {
        const el = this.container.querySelector(`[data-sl-status="${loc.id}"]`);
        if (!el) return;
        const status = this.isOpenNow(loc.opening_hours);
        el.textContent = status.label;
        el.classList.toggle('store-locator-card__status--open', status.open);
        el.classList.toggle('store-locator-card__status--closed', !status.open);
      });
    }

    /* -----------------------------------------------------------------------
       Map Markers — All markers + filtered markers
       ----------------------------------------------------------------------- */

    _addAllMarkers() {
      if (!this.map) return;
      this.clearMarkers();

      this.locations.forEach((loc) => {
        const markerOpts = {
          position: { lat: loc.lat, lng: loc.lng },
          map: this.map,
          title: loc.name,
        };
        if (loc.pin) {
          markerOpts.icon = {
            url: loc.pin,
            scaledSize: new google.maps.Size(36, 36),
            anchor: new google.maps.Point(18, 36),
          };
        }
        const marker = new google.maps.Marker(markerOpts);
        marker._locationId = loc.id;
        marker._chain = loc.chain || '';
        marker.addListener('click', () => this.openInfoWindow(loc, marker));
        this.markers.push(marker);
      });

      this._applyClustering();

      // Fit to all of Italy
      if (this.locations.length > 0) {
        this.map.setCenter({ lat: 42.5, lng: 12.5 });
        this.map.setZoom(6);
      }
    }

    _updateMapMarkers() {
      if (!this.map) return;
      this.clearMarkers();

      const locsToShow = this.filteredLocations;
      locsToShow.forEach((loc) => {
        const markerOpts = {
          position: { lat: loc.lat, lng: loc.lng },
          map: this.map,
          title: loc.name,
        };
        if (loc.pin) {
          markerOpts.icon = {
            url: loc.pin,
            scaledSize: new google.maps.Size(36, 36),
            anchor: new google.maps.Point(18, 36),
          };
        }
        const marker = new google.maps.Marker(markerOpts);
        marker._locationId = loc.id;
        marker._chain = loc.chain || '';
        marker.addListener('click', () => this.openInfoWindow(loc, marker));
        this.markers.push(marker);
      });

      this._applyClustering();

      if (locsToShow.length > 0 && this.center) {
        this._fitBounds(locsToShow);
      } else if (locsToShow.length > 0 && !this.center) {
        this._fitBounds(locsToShow);
      }
    }

    addMarkers(locations) {
      this._updateMapMarkers();
    }

    clearMarkers() {
      this._clearClusterers();
      this.markers.forEach((m) => m.setMap(null));
      this.markers = [];
    }

    _clearClusterers() {
      if (this.clusterers && this.clusterers.length) {
        this.clusterers.forEach((c) => c.clearMarkers());
      }
      this.clusterers = [];
      this.clusterer = null;
    }

    _applyClustering() {
      if (!window.markerClusterer || !this.map) return;
      this._clearClusterers();

      try {
        // Un clusterer per ogni catena: il cluster usa il PIN piccolo della
        // catena (lo stesso simbolo dei singoli punti) + badge col conteggio.
        const Algo = window.markerClusterer.SuperClusterAlgorithm;
        const groups = new Map();
        this.markers.forEach((m) => {
          const key = m._chain || '';
          if (!groups.has(key)) groups.set(key, []);
          groups.get(key).push(m);
        });
        this.clusterers = [];
        groups.forEach((markers, chain) => {
          if (markers.length < 2) return; // un solo punto non si clusterizza
          const opts = {
            map: this.map,
            markers,
            renderer: this._makeChainRenderer(chain),
          };
          // Raggruppamento più aggressivo (raggio maggiore) = mappa più pulita.
          if (Algo) opts.algorithm = new Algo({ radius: 90, maxZoom: 12 });
          this.clusterers.push(new markerClusterer.MarkerClusterer(opts));
        });
      } catch (e) {
        console.warn('[StoreLocator] errore clustering:', e);
      }
    }

    // Renderer del cluster per catena: usa il PIN piccolo della catena
    // (Arcaplanet, Conad, ecc. — lo stesso simbolo dei singoli punti) + badge.
    _makeChainRenderer(chain) {
      const pin = (this._chainPins && this._chainPins[chain]) || this._pinIndip || '';
      return {
        render: (cluster) => {
          const count = cluster.count;
          const position = cluster.position;
          const size = 36; // stessa dimensione dei singoli pin
          const icon = pin
            ? {
                url: pin,
                scaledSize: new google.maps.Size(size, size),
                anchor: new google.maps.Point(size / 2, size / 2),
                labelOrigin: new google.maps.Point(size - 5, 5),
              }
            : undefined;
          return new google.maps.Marker({
            position,
            icon,
            label: {
              text: String(count),
              className: 'sl-cluster-count',
              color: '#ffffff',
              fontSize: '11px',
              fontWeight: '700',
            },
            title: chain || 'Indipendenti',
            zIndex: 1000 + count,
          });
        },
      };
    }

    clusterMarkers() {
      this._applyClustering();
    }

    // Chiamato quando la libreria MarkerClusterer finisce di caricarsi (async).
    _onClustererReady() {
      if (this.map && this.markers.length > 0 && (!this.clusterers || !this.clusterers.length)) {
        this._applyClustering();
      }
    }

    openInfoWindow(location, marker) {
      if (!this.infoWindow || !this.map) return;
      const directionsUrl = this.getDirectionsUrl(location);
      const status = this.isOpenNow(location.opening_hours);

      // Logo or chain badge
      const iwIcon = this._iconFor(location);
      const logoHtml = iwIcon
        ? `<img class="sl-iw__logo" src="${this._esc(iwIcon)}" alt="${this._esc(location.chain)}">`
        : '';

      // Status badge
      const statusClass = status.open ? 'sl-iw__status--open' : 'sl-iw__status--closed';
      const statusHtml = status.label
        ? `<span class="sl-iw__status ${statusClass}">${status.label}</span>`
        : '';

      // Hours (compact)
      const hoursHtml = this._renderInfoWindowHours(location.opening_hours);

      // Product tags (+ badge CatHeroes se marchio cat, solo in visualizzazione)
      const iwTags = Array.isArray(location.tags) ? location.tags.slice() : [];
      if (this._brandsForLoc(location).includes('cat')) iwTags.push('CatHeroes');
      const tagsHtml = iwTags.length > 0
        ? `<div class="sl-iw__tags">${iwTags.map((t) => {
            const imgUrl = this._getProductImageUrl(t);
            const iwCls = t === 'CatHeroes' ? 'sl-iw__tag-icon sl-iw__tag-icon--cat' : 'sl-iw__tag-icon';
            return imgUrl
              ? `<span class="sl-iw__tag"><img class="${iwCls}" src="${imgUrl}" alt="${this._esc(t)}"><span>${this._esc(t)}</span></span>`
              : `<span class="sl-iw__tag"><span>${this._esc(t)}</span></span>`;
          }).join('')}</div>`
        : '';

      // Phone action
      const phoneHtml = location.phone
        ? `<a class="sl-iw__action" href="tel:${this._esc(location.phone)}">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" width="14" height="14"><path d="M6.62 10.79a15.05 15.05 0 006.59 6.59l2.2-2.2a1 1 0 011.01-.24 11.36 11.36 0 003.58.57 1 1 0 011 1V20a1 1 0 01-1 1A17 17 0 013 4a1 1 0 011-1h3.5a1 1 0 011 1c0 1.25.2 2.45.57 3.57a1 1 0 01-.25 1.02l-2.2 2.2z"/></svg>
            Chiama
          </a>`
        : '';

      this.infoWindow.setContent(`
        <div class="sl-iw">
          <div class="sl-iw__header">
            <div>
              ${location.chain ? `<span class="sl-iw__chain">${this._esc(location.chain)}</span>` : ''}
              <h4 class="sl-iw__name">${this._esc(location.name)}</h4>
              <p class="sl-iw__address">${this._formatAddress(location)}</p>
            </div>
            ${logoHtml}
          </div>
          ${statusHtml}
          ${hoursHtml}
          ${tagsHtml}
          <div class="sl-iw__actions">
            <a class="sl-iw__action" href="${directionsUrl}" target="_blank" rel="noopener">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" width="14" height="14"><path d="M22.46 10.12L13.88 1.54a2 2 0 00-2.83-.01l-.01.01-8.58 8.58a2 2 0 000 2.83l8.58 8.58a2 2 0 002.83 0l8.58-8.58a2 2 0 00.01-2.83zM13 17h-2v-2h2v2zm0-4h-2V7h2v6z"/></svg>
              Indicazioni
            </a>
            ${phoneHtml}
          </div>
        </div>
      `);
      this.infoWindow.open(this.map, marker);
      this.map.panTo(marker.getPosition());
      this.activeCardId = location.id;
      this._highlightCard(location.id);
    }

    _renderInfoWindowHours(hours) {
      if (!hours) return '';
      const dayLabels = { mon: 'Lun', tue: 'Mar', wed: 'Mer', thu: 'Gio', fri: 'Ven', sat: 'Sab', sun: 'Dom' };
      const dayKeys = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'];
      const todayKey = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'][new Date().getDay()];
      const rows = dayKeys.map((key) => {
        const val = hours[key] || '';
        const display = val === 'closed' ? 'Chiuso' : val.replace('-', ' – ');
        const isToday = key === todayKey;
        return `<tr${isToday ? ' class="sl-iw__hours-today"' : ''}>
          <td style="padding:1px 8px 1px 0;font-weight:${isToday ? '600' : '400'};color:${isToday ? '#333' : '#666'}">${dayLabels[key]}</td>
          <td style="padding:1px 0;color:${isToday ? '#333' : '#666'}">${display || '–'}</td>
        </tr>`;
      }).join('');
      return `<table style="font-size:12px;border-collapse:collapse;margin:6px 0">${rows}</table>`;
    }

    _fitBounds(locations) {
      if (!this.map || locations.length === 0) return;
      const bounds = new google.maps.LatLngBounds();
      locations.forEach((loc) => bounds.extend({ lat: loc.lat, lng: loc.lng }));
      if (this.center) bounds.extend(this.center);
      this.map.fitBounds(bounds);
      if (locations.length === 1) this.map.setZoom(14);
    }

    _selectLocation(id) {
      const loc = this.filteredLocations.find((l) => l.id === id);
      if (!loc) return;
      this.activeCardId = id;
      this._highlightCard(id);
      if (this.map) {
        const marker = this.markers.find((m) => m._locationId === id);
        if (marker) this.openInfoWindow(loc, marker);
      }
    }

    _highlightCard(id) {
      this.container.querySelectorAll('[data-sl-card]').forEach((card) => {
        card.classList.toggle('store-locator-card--active', card.dataset.slCard === id);
      });
    }

    /* -----------------------------------------------------------------------
       Utility
       ----------------------------------------------------------------------- */

    formatDistance(km) {
      return km < 1 ? `${Math.round(km * 1000)} m` : `${km.toFixed(1)} km`;
    }

    getDirectionsUrl(location) {
      const dest = encodeURIComponent(`${location.address}, ${location.zip} ${location.city}`);
      const isApple = /iPhone|iPad|iPod|Macintosh/.test(navigator.userAgent) && navigator.maxTouchPoints > 0;
      return isApple
        ? `https://maps.apple.com/?daddr=${dest}`
        : `https://www.google.com/maps/dir/?api=1&destination=${dest}`;
    }

    _getTypeLabel(type) {
      return { retail: this.options.labelRetail, rivenditore: this.options.labelRivenditore, veterinario: this.options.labelVeterinario }[type] || type;
    }

    _showLoading(show) {
      if (this._refs.loading) this._refs.loading.hidden = !show;
      if (this._refs.resultsList) this._refs.resultsList.hidden = show;
    }

    _toggleMap() {
      if (!this._refs.mapEl) return;
      this.mapVisible = !this.mapVisible;
      this._refs.mapEl.classList.toggle('store-locator__map--collapsed', !this.mapVisible);
      if (this._refs.mapToggle) this._refs.mapToggle.setAttribute('aria-expanded', String(this.mapVisible));
      if (this.mapVisible && !this.map) this.initMap();
      else if (this.mapVisible && this.map) {
        google.maps.event.trigger(this.map, 'resize');
        this._fitBounds(this.filteredLocations);
      }
    }

    _formatAddress(loc) {
      const parts = [];
      if (loc.address) parts.push(this._esc(loc.address));
      const cityPart = [loc.zip, loc.city].filter(Boolean).join(' ');
      if (cityPart) parts.push(this._esc(cityPart));
      return parts.join(', ');
    }

    _esc(str) {
      if (!str) return '';
      const el = document.createElement('span');
      el.textContent = str;
      return el.innerHTML;
    }

    /* -----------------------------------------------------------------------
       Public API
       ----------------------------------------------------------------------- */

    setFilter(key, value) {
      if (key === 'type') { this.activeFilter = value; if (this._refs.typeSelect) this._refs.typeSelect.value = value; }
      if (key === 'chain') { this.activeChains.clear(); if (value) this.activeChains.add(value); this._updateChainPillStates(); }
      this._applyAndRender();
    }

    setRadius(km) { this.options.radiusKm = km; if (this.center) this._applyAndRender(); }
    setCenter(lat, lng) { this.center = { lat, lng }; this._applyAndRender(); }
    getResults() { return [...this.filteredLocations]; }
  }

  /* =========================================================================
     Auto-init
     ========================================================================= */

  function initAll() {
    document.querySelectorAll('[data-store-locator]').forEach((container) => {
      if (container.dataset.slInitialized) return;
      container.dataset.slInitialized = 'true';
      const locator = new DogHeroesStoreLocator(container);
      locator.init();
      setInterval(() => locator._updateOpenStatus(), 60000);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', (e) => {
    const container = e.target.querySelector('[data-store-locator]');
    if (container) {
      container.removeAttribute('data-sl-initialized');
      const locator = new DogHeroesStoreLocator(container);
      locator.init();
    }
  });

  window.DogHeroesLocator = { instances: INSTANCES, DogHeroesStoreLocator, init: initAll };
})();